import { useDynamicMessageContext } from '@components/Home/Alerts/DynamicMessageContext';
import { parsePayload } from '@hooks/useSubscription';
import { AlertEntity } from '@src/types/Alerts';
import { MqttClient } from 'mqtt';
import { useEffect, useState } from 'react';

function DynamicAlertMessage({
  alertTopic,
  defaultMessage,
}: {
  alertTopic: AlertEntity['topic'];
  defaultMessage: AlertEntity['message'];
}) {
  const messageTopic = alertTopic
    .split('/')
    .slice(0, -1)
    .concat('message')
    .join('/');
  const [dynamicMessage, setDynamicMessage] = useState<string | number | null>(
    defaultMessage,
  );
  const { mqttClient: dynamicMessageMqttClient } = useDynamicMessageContext();

  useEffect(() => {
    if (dynamicMessageMqttClient && messageTopic) {
      // Function to handle received messages
      const handleMessage = (topic: string, message: Buffer) => {
        if (topic === messageTopic) {
          const { value } = parsePayload(message, topic);
          if (typeof value !== 'string' || value.length === 0) {
            return setDynamicMessage(defaultMessage);
          } else {
            setDynamicMessage(value);
          }
        }
      };

      const handleMqttUnsubscribe = (
        client: MqttClient,
        topic: string,
      ): void => {
        try {
          client.unsubscribe(topic);
        } catch (err) {
          // Only ignore specific disconnection errors, throw all others
          if (
            !(err instanceof Error && err.message === 'client disconnecting')
          ) {
            throw new Error(
              `Failed to unsubscribe from MQTT topic ${topic}: ${err}`,
            );
          }
          // Disconnection errors can be safely ignored during shutdown
        }
      };

      // Subscribe to the topic
      dynamicMessageMqttClient.subscribe(messageTopic);
      dynamicMessageMqttClient.on('message', handleMessage);

      // Unsubscribe and clean up on component unmount
      return () => {
        handleMqttUnsubscribe(dynamicMessageMqttClient, messageTopic);
        dynamicMessageMqttClient.off('message', handleMessage);
      };
    }
  }, [defaultMessage, dynamicMessageMqttClient, messageTopic]);

  return dynamicMessage;
}

export default DynamicAlertMessage;
